import { fetchJSON as fetchAPI } from './client';

export type Scenario = {
  id: string;
  name: string;
  scenario_type: string;
  status: string;
  description?: string;
  composite_risk_score: number;
  planning_confidence_score: number;
  enterprise_readiness_score: number;
  optimization_goal: string;
};

export type SimulationResult = {
  id: string;
  scenario_id: string;
  demand_impact_pct: number;
  forecast_impact_pct: number;
  inventory_impact_pct: number;
  supply_impact_pct: number;
  warehouse_impact_pct: number;
  service_level: number;
  working_capital: number;
  revenue: number;
  composite_risk_score: number;
  confidence: number;
  explanation: string;
};

export type ScenarioComparison = {
  scenario_ids: string[];
  results: SimulationResult[];
  best_scenario_id?: string;
  comparison_note: string;
};

export type EnterpriseDigitalTwin = {
  id: string;
  demand_nodes: number;
  forecast_nodes: number;
  inventory_nodes: number;
  procurement_nodes: number;
  supply_nodes: number;
  warehouse_nodes: number;
  constraint_count: number;
  module_sync: Record<string, string>;
  utilization_map: Record<string, number>;
  bottleneck_nodes: string[];
};

export type ScenarioConstraint = {
  id: string;
  type: string;
  severity: string;
  probability: number;
  description: string;
  impact: string;
  mitigation: string;
  violated: boolean;
};

export type ScenarioRisk = {
  id: string;
  risk_type: string;
  severity: string;
  probability: number;
  business_impact: string;
  mitigation_plan: string;
};

export type SimulationRecommendation = {
  id: string;
  type: string;
  title: string;
  description: string;
  business_explanation: string;
  confidence: number;
  expected_savings?: number;
  service_impact?: string;
  auto_execute: boolean;
  status: string;
};

export type ScenarioTemplate = {
  id: string;
  name: string;
  scenario_type: string;
  description: string;
};

export type ScenarioDashboard = {
  active_scenarios: number;
  simulations_run: number;
  avg_confidence: number;
  composite_risk_score: number;
  planning_confidence_score: number;
  enterprise_readiness_score: number;
  constraint_violations: number;
  pending_recommendations: number;
  scenario_reuse_rate: number;
  decision_acceptance_rate: number;
};

const API_BASE = import.meta.env.VITE_SCENARIOPLANNING_API ?? '/api/v1';

function scenFetch<T>(path: string, init?: RequestInit): Promise<T> {
  return fetchAPI<T>(API_BASE, path, init);
}

export const scenarioPlanningApi = {
  dashboard: () => scenFetch<ScenarioDashboard>('/scenario-dashboard'),
  listScenarios: () => scenFetch<Scenario[]>('/scenarios'),
  getScenario: (id: string) => scenFetch<Scenario>(`/scenarios/${id}`),
  simulate: (scenarioId: string) =>
    scenFetch<SimulationResult>('/scenarios/simulate', {
      method: 'POST',
      body: JSON.stringify({ scenario_id: scenarioId }),
    }),
  compare: (ids: string[]) =>
    scenFetch<ScenarioComparison>(`/scenarios/compare?${ids.map((id) => `ids=${id}`).join('&')}`),
  getDigitalTwin: () => scenFetch<EnterpriseDigitalTwin>('/digital-twin'),
  listConstraints: (scenarioId?: string) =>
    scenFetch<ScenarioConstraint[]>(`/scenario-constraints${scenarioId ? `?scenario_id=${scenarioId}` : ''}`),
  listRisks: (scenarioId?: string) =>
    scenFetch<ScenarioRisk[]>(`/scenario-risks${scenarioId ? `?scenario_id=${scenarioId}` : ''}`),
  listRecommendations: (scenarioId?: string) =>
    scenFetch<SimulationRecommendation[]>(`/scenario-recommendations${scenarioId ? `?scenario_id=${scenarioId}` : ''}`),
  listTemplates: () => scenFetch<ScenarioTemplate[]>('/scenario-templates'),
  approveScenario: (id: string) => scenFetch<Scenario>(`/scenarios/${id}/approve`, { method: 'POST' }),
};
